/**
 * ProtectedRoute - Route wrapper that requires authentication
 * Shows Authenticator if user is not logged in
 */

import { Authenticator } from '@aws-amplify/ui-react';
import { signUp, confirmSignUp } from 'aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';
import { theme } from '../constants/theme.js';
import { getStoredRef } from '../utils/referral.js';
import { getAttributionMetadata } from '../utils/attribution.js';

// Attach the stored referral code and first-touch attribution to sign-up /
// confirm as Cognito clientMetadata. confirmSignUp is the one that reaches the
// PostConfirmation trigger (event.request.clientMetadata.ref / .attr), where
// both are written atomically with the new profile; we set them on signUp too
// for completeness.
function withSignupMetadata(input) {
  const ref = getStoredRef();
  const attr = getAttributionMetadata();
  if (!ref && !attr) return input;
  const clientMetadata = {
    ...(input.options?.clientMetadata || {}),
    ...(ref ? { ref } : {}),
    ...(attr ? { attr } : {}),
  };
  return { ...input, options: { ...input.options, clientMetadata } };
}

const signupServices = {
  async handleSignUp(input) {
    return signUp(withSignupMetadata(input));
  },
  async handleConfirmSignUp(input) {
    return confirmSignUp(withSignupMetadata(input));
  },
};

// CSS overrides to make Amplify Authenticator match Atlas Casino dark theme
const authStyles = `
  [data-amplify-authenticator] {
    --amplify-colors-background-primary: ${theme.bgCard};
    --amplify-colors-background-secondary: ${theme.bg};
    --amplify-colors-background-tertiary: rgba(255,255,255,0.03);
    --amplify-colors-border-primary: ${theme.borderLight};
    --amplify-colors-border-secondary: ${theme.border};
    --amplify-colors-border-focus: ${theme.purple};
    --amplify-colors-font-primary: ${theme.t1};
    --amplify-colors-font-secondary: ${theme.t3};
    --amplify-colors-font-tertiary: ${theme.t4};
    --amplify-colors-font-interactive: ${theme.purple};
    --amplify-colors-font-hover: ${theme.purpleLight};
    --amplify-colors-font-focus: ${theme.purple};
    --amplify-colors-font-active: ${theme.purpleLight};
    --amplify-colors-brand-primary-10: rgba(123,47,247,0.1);
    --amplify-colors-brand-primary-20: rgba(123,47,247,0.2);
    --amplify-colors-brand-primary-40: rgba(123,47,247,0.4);
    --amplify-colors-brand-primary-60: ${theme.purple};
    --amplify-colors-brand-primary-80: ${theme.purpleLight};
    --amplify-colors-brand-primary-90: #c4a0fc;
    --amplify-colors-brand-primary-100: #e0d0fe;
    --amplify-components-authenticator-router-background-color: ${theme.bgCard};
    --amplify-components-authenticator-router-border-color: ${theme.borderLight};
    --amplify-components-authenticator-router-border-width: 1px;
    --amplify-components-authenticator-router-box-shadow: 0 25px 50px rgba(0,0,0,0.5);
    --amplify-components-authenticator-form-padding: 2rem;
    --amplify-components-button-primary-background-color: ${theme.purple};
    --amplify-components-button-primary-hover-background-color: #9b5ff9;
    --amplify-components-button-primary-focus-background-color: #9b5ff9;
    --amplify-components-button-primary-active-background-color: #6920d0;
    --amplify-components-button-primary-color: #ffffff;
    --amplify-components-button-link-color: ${theme.purple};
    --amplify-components-button-link-hover-color: ${theme.purpleLight};
    --amplify-components-button-link-hover-background-color: transparent;
    --amplify-components-fieldcontrol-border-color: ${theme.borderLight};
    --amplify-components-fieldcontrol-focus-border-color: ${theme.purple};
    --amplify-components-fieldcontrol-color: ${theme.t1};
    --amplify-components-fieldcontrol-background-color: rgba(255,255,255,0.03);
    --amplify-components-field-label-color: ${theme.t3};
    --amplify-components-tabs-item-active-border-color: ${theme.purple};
    --amplify-components-tabs-item-active-color: ${theme.purple};
    --amplify-components-tabs-item-color: ${theme.t3};
    --amplify-components-tabs-item-hover-color: ${theme.t2};
    --amplify-components-tabs-border-color: ${theme.border};
    --amplify-components-heading-color: ${theme.t1};
    --amplify-components-text-color: ${theme.t3};
    --amplify-radii-small: 8px;
    --amplify-radii-medium: 12px;
    --amplify-radii-large: 16px;
    font-family: ${theme.fontPrimary};
  }

  [data-amplify-authenticator] [data-amplify-router] {
    background: ${theme.bgCard};
    border: 1px solid ${theme.borderLight};
    border-radius: 16px;
    box-shadow: 0 25px 50px rgba(0,0,0,0.5);
    overflow: hidden;
  }

  [data-amplify-authenticator] button[data-variation="primary"] {
    background: linear-gradient(135deg, ${theme.purple}, #9333EA);
    border: none;
    border-radius: 10px;
    font-weight: 600;
    letter-spacing: 0.3px;
    padding: 12px 24px;
    transition: all 0.2s ease;
  }

  [data-amplify-authenticator] button[data-variation="primary"]:hover {
    background: linear-gradient(135deg, #8b3ff7, #a040f0);
    box-shadow: ${theme.purpleGlow};
    transform: translateY(-1px);
  }

  [data-amplify-authenticator] button[data-variation="link"] {
    color: ${theme.purple};
  }

  [data-amplify-authenticator] button[data-variation="link"]:hover {
    color: ${theme.purpleLight};
    background: transparent;
  }

  [data-amplify-authenticator] input,
  [data-amplify-authenticator] .amplify-input {
    background: rgba(255,255,255,0.03) !important;
    border-color: ${theme.borderLight} !important;
    color: ${theme.t1} !important;
    border-radius: 10px !important;
  }

  [data-amplify-authenticator] input:focus,
  [data-amplify-authenticator] .amplify-input:focus {
    border-color: ${theme.purple} !important;
    box-shadow: 0 0 0 2px rgba(123,47,247,0.2) !important;
  }

  [data-amplify-authenticator] input::placeholder {
    color: ${theme.t4} !important;
  }

  [data-amplify-authenticator] .amplify-field__show-password {
    color: ${theme.t4};
    background: transparent;
    border-color: ${theme.borderLight};
  }

  [data-amplify-authenticator] .amplify-field__show-password:hover {
    color: ${theme.t2};
    background: rgba(255,255,255,0.05);
  }

  [data-amplify-authenticator] .amplify-tabs__item {
    color: ${theme.t3};
    border-bottom: 2px solid transparent;
    transition: all 0.2s ease;
  }

  [data-amplify-authenticator] .amplify-tabs__item:hover {
    color: ${theme.t2};
  }

  [data-amplify-authenticator] .amplify-tabs__item[data-state="active"] {
    color: ${theme.purple};
    border-bottom-color: ${theme.purple};
  }

  [data-amplify-authenticator] .amplify-tabs__list {
    border-bottom-color: ${theme.border};
  }

  [data-amplify-authenticator] .amplify-alert--error {
    background: ${theme.errorBg};
    border-color: rgba(239,68,68,0.3);
    color: ${theme.error};
    border-radius: 10px;
  }
`;

export default function ProtectedRoute({ children }) {
  // Land on the sign-up tab when the user arrived with `?signup=1` or via a
  // referral link (a stored ref implies they came to join, not to sign in).
  const wantsSignUp = typeof window !== 'undefined'
    && (new URLSearchParams(window.location.search).get('signup') === '1' || !!getStoredRef());
  const initialState = wantsSignUp ? 'signUp' : 'signIn';
  return (
    <div
      className="h-dvh flex items-center justify-center overflow-y-auto"
      style={{ backgroundColor: theme.bg }}
    >
      <style>{authStyles}</style>
      <Authenticator
        initialState={initialState}
        services={signupServices}
        signUpAttributes={['email']}
        loginMechanisms={['email']}
        components={{
          Header() {
            return (
              <div className="text-center py-6">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl font-bold"
                    style={{ background: theme.grad, boxShadow: theme.purpleGlow }}
                  >
                    A
                  </div>
                  <span className="text-2xl font-bold text-white">
                    ATLAS<span style={{ fontWeight: 400, color: theme.t3, marginLeft: 6 }}>Casino</span>
                  </span>
                </div>
                <p className="text-sm" style={{ color: theme.t4 }}>
                  Sign in to access your account
                </p>
                <p className="text-xs mt-1" style={{ color: theme.t5 }}>
                  For players in international waters only
                </p>
              </div>
            );
          },
        }}
      >
        {({ signOut, user }) => children}
      </Authenticator>
    </div>
  );
}
